import type { Currency } from '@kmdx/contracts';

/**
 * Форматиране за екрана. Часовата зона е фиксирана на офиса в Пловдив —
 * диспечерът мисли в местно време, дори когато бусът е в Щутгарт, а базата
 * пази всичко в UTC.
 */
export const OFFICE_TZ = 'Europe/Sofia';
const LOCALE = 'bg-BG';

const dateFmt = new Intl.DateTimeFormat(LOCALE, {
  timeZone: OFFICE_TZ,
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

const dateShortFmt = new Intl.DateTimeFormat(LOCALE, {
  timeZone: OFFICE_TZ,
  day: '2-digit',
  month: '2-digit',
});

const timeFmt = new Intl.DateTimeFormat(LOCALE, {
  timeZone: OFFICE_TZ,
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

const dateTimeFmt = new Intl.DateTimeFormat(LOCALE, {
  timeZone: OFFICE_TZ,
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

const weekdayFmt = new Intl.DateTimeFormat(LOCALE, { timeZone: OFFICE_TZ, weekday: 'short' });
const monthFmt = new Intl.DateTimeFormat(LOCALE, { timeZone: OFFICE_TZ, month: 'short', year: '2-digit' });

export function toDate(value: string | number | Date | null | undefined): Date | null {
  if (value === null || value === undefined || value === '') return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

export const formatDate = (v: string | Date | null | undefined): string => {
  const d = toDate(v);
  return d ? dateFmt.format(d) : '—';
};

export const formatDateShort = (v: string | Date | null | undefined): string => {
  const d = toDate(v);
  return d ? dateShortFmt.format(d) : '—';
};

export const formatTime = (v: string | Date | null | undefined): string => {
  const d = toDate(v);
  return d ? timeFmt.format(d) : '—';
};

export const formatDateTime = (v: string | Date | null | undefined): string => {
  const d = toDate(v);
  return d ? dateTimeFmt.format(d) : '—';
};

export const formatWeekday = (v: string | Date | null | undefined): string => {
  const d = toDate(v);
  return d ? weekdayFmt.format(d) : '—';
};

export const formatMonth = (v: string | Date | null | undefined): string => {
  const d = toDate(v);
  return d ? monthFmt.format(d) : '—';
};

/** „днес 14:20", „утре 06:00", иначе „21.08 14:20" — както го казва диспечерът. */
export function formatWhen(v: string | Date | null | undefined, now: Date = new Date()): string {
  const d = toDate(v);
  if (!d) return '—';
  const dayKey = (x: Date) => dateFmt.format(x);
  const tomorrow = new Date(now.getTime() + 24 * 60 * 60 * 1000);
  const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000);
  if (dayKey(d) === dayKey(now)) return `днес ${timeFmt.format(d)}`;
  if (dayKey(d) === dayKey(tomorrow)) return `утре ${timeFmt.format(d)}`;
  if (dayKey(d) === dayKey(yesterday)) return `вчера ${timeFmt.format(d)}`;
  return `${dateShortFmt.format(d)} ${timeFmt.format(d)}`;
}

/** „преди 3 мин" — за възрастта на последната позиция. */
export function formatAgo(v: string | Date | null | undefined, now: Date = new Date()): string {
  const d = toDate(v);
  if (!d) return '—';
  const min = Math.round((now.getTime() - d.getTime()) / 60000);
  if (min < 1) return 'сега';
  if (min < 60) return `преди ${min} мин`;
  const h = Math.floor(min / 60);
  if (h < 24) return `преди ${h} ч`;
  const days = Math.floor(h / 24);
  return `преди ${days} дн`;
}

/** Минути в „7:45" — диспечерът чете часовете, не десетичните дроби. */
export function formatHm(minutes: number | null | undefined): string {
  if (minutes === null || minutes === undefined || !Number.isFinite(minutes)) return '—';
  const total = Math.max(0, Math.round(minutes));
  const h = Math.floor(total / 60);
  const m = total % 60;
  return `${h}:${String(m).padStart(2, '0')}`;
}

export function formatDuration(minutes: number | null | undefined): string {
  if (minutes === null || minutes === undefined || !Number.isFinite(minutes)) return '—';
  const total = Math.max(0, Math.round(minutes));
  const days = Math.floor(total / (24 * 60));
  const h = Math.floor((total % (24 * 60)) / 60);
  const m = total % 60;
  if (days > 0) return `${days} д ${h} ч`;
  if (h > 0) return `${h} ч ${m} мин`;
  return `${m} мин`;
}

const moneyFormatters = new Map<string, Intl.NumberFormat>();

function moneyFormatter(ccy: string, fractionDigits: number): Intl.NumberFormat {
  const key = `${ccy}:${fractionDigits}`;
  let fmt = moneyFormatters.get(key);
  if (!fmt) {
    fmt = new Intl.NumberFormat(LOCALE, {
      style: 'currency',
      currency: ccy,
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    });
    moneyFormatters.set(key, fmt);
  }
  return fmt;
}

/** Сумите идват от API-то в най-малката единица (евроцентове). */
export function formatMinor(
  minor: number | null | undefined,
  ccy: Currency | string = 'EUR',
  opts: { compact?: boolean } = {},
): string {
  if (minor === null || minor === undefined || !Number.isFinite(minor)) return '—';
  const digits = opts.compact ? 0 : 2;
  return moneyFormatter(ccy, digits).format(minor / 100);
}

export function formatSignedMinor(minor: number | null | undefined, ccy: Currency | string = 'EUR'): string {
  if (minor === null || minor === undefined || !Number.isFinite(minor)) return '—';
  const text = formatMinor(Math.abs(minor), ccy);
  return minor < 0 ? `−${text}` : text;
}

const numberFmt = new Intl.NumberFormat(LOCALE, { maximumFractionDigits: 0 });
const decimalFmt = new Intl.NumberFormat(LOCALE, { maximumFractionDigits: 1 });

export const formatNumber = (n: number | null | undefined): string =>
  n === null || n === undefined || !Number.isFinite(n) ? '—' : numberFmt.format(n);

export const formatKm = (km: number | null | undefined): string =>
  km === null || km === undefined || !Number.isFinite(km) ? '—' : `${numberFmt.format(Math.round(km))} км`;

export const formatKg = (kg: number | null | undefined): string =>
  kg === null || kg === undefined || !Number.isFinite(kg) ? '—' : `${numberFmt.format(kg)} кг`;

/** Базисни пунктове → процент. 1834 bps = „18,3 %" */
export const formatBps = (bps: number | null | undefined): string =>
  bps === null || bps === undefined || !Number.isFinite(bps) ? '—' : `${decimalFmt.format(bps / 100)} %`;

export const formatPct = (ratio: number | null | undefined): string =>
  ratio === null || ratio === undefined || !Number.isFinite(ratio)
    ? '—'
    : `${decimalFmt.format(ratio * 100)} %`;

/** Координати × 1e7 в четим вид за подсказка на картата. */
export const formatCoord = (e7: number | null | undefined): string =>
  e7 === null || e7 === undefined ? '—' : (e7 / 1e7).toFixed(5);

/** „PB1234AH" се чете по-лесно като „PB 1234 AH". */
export function formatPlate(plate: string | null | undefined): string {
  if (!plate) return '—';
  const clean = plate.replace(/\s+/g, '').toUpperCase();
  const m = /^([A-ZА-Я]{1,2})(\d{4})([A-ZА-Я]{2})$/.exec(clean);
  return m ? `${m[1]} ${m[2]} ${m[3]}` : plate;
}

export function initials(name: string | null | undefined): string {
  if (!name) return '—';
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.[0] ?? '';
  const second = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return (first + second).toUpperCase();
}

/** Колко дни остават до дата — база за предупрежденията за документи. */
export function daysUntil(value: string | Date | null | undefined, now: Date = new Date()): number | null {
  const d = toDate(value);
  if (!d) return null;
  return Math.floor((d.getTime() - now.getTime()) / (24 * 60 * 60 * 1000));
}

export const isoDate = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};
